import React, { useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import Hero from '../components/Hero'
import { SliderDataTwelve } from '../data/SliderData'

/* ─── Animations ─────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`

/* ─── Page ───────────────────────────────────────────────────── */
const PageWrapper = styled.div`
  background: #f0e9e0;
  color: #2c1f14;
`

/* ─── Intro Section ──────────────────────────────────────────── */
const IntroSection = styled.section`
  padding: 110px 24px 90px;
  text-align: center;
  background: #f0e9e0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 50%; transform: translateX(-50%);
    width: 1px;
    height: 70px;
    background: linear-gradient(180deg, transparent, #b07d3a);
  }
`

const Eyebrow = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.45em;
  text-transform: uppercase;
  color: #b07d3a;
  margin-bottom: 18px;
  animation: ${fadeUp} 0.8s ease both;
`

const PageHeading = styled.h1`
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 300;
  color: #2c1f14;
  margin: 0 auto 20px;
  line-height: 1.1;
  letter-spacing: -0.01em;
  animation: ${fadeUp} 0.85s 0.05s ease both;
`

const OrnamentDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0 auto 36px;
  max-width: 220px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b07d3a);
  }
  &::after {
    background: linear-gradient(90deg, #b07d3a, transparent);
  }
`

const OrnamentDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #b07d3a;
  flex-shrink: 0;
`

const IntroBody = styled.p`
  font-size: clamp(1.05rem, 1.5vw, 1.25rem);
  line-height: 1.9;
  color: #7a5c44;
  max-width: 720px;
  margin: 0 auto;
  font-style: italic;
  animation: ${fadeUp} 0.9s 0.15s ease both;
`

/* ─── Links Section ──────────────────────────────────────────── */
const LinksSection = styled.section`
  padding: 0 24px 110px;
  background: #f0e9e0;
`

const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 500px;
  }
`

const LinkCard = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 44px 36px 36px;
  border: 1px solid rgba(176, 125, 58, 0.2);
  border-radius: 2px;
  background: #e8ddd0;
  text-decoration: none;
  color: #2c1f14;
  transition: border-color 0.35s, transform 0.35s, box-shadow 0.35s;
  overflow: hidden;
  animation: ${fadeUp} 0.8s ease both;
  animation-delay: ${p => p.delay || '0s'};

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #b07d3a, transparent);
    opacity: 0;
    transition: opacity 0.35s;
  }

  &:hover {
    border-color: rgba(176, 125, 58, 0.5);
    transform: translateY(-6px);
    box-shadow: 0 24px 56px rgba(44, 31, 20, 0.12);
    &::before { opacity: 1; }
    .card-arrow { transform: translateX(6px); }
  }
`

const CardNumber = styled.span`
  font-size: 0.65rem;
  letter-spacing: 0.3em;
  color: #b07d3a;
  display: block;
  margin-bottom: 20px;
  text-transform: uppercase;
`

const CardTitle = styled.h4`
  font-size: clamp(1.3rem, 1.8vw, 1.6rem);
  font-weight: 400;
  color: #2c1f14;
  margin: 0 0 14px;
  line-height: 1.3;
`

const CardDesc = styled.p`
  font-size: 0.95rem;
  color: #8a7560;
  line-height: 1.75;
  margin: 0 0 28px;
`

const CardArrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #b07d3a;
  transition: transform 0.3s;
`

/* ─── Menu Section ───────────────────────────────────────────── */
const MenuSection = styled.section`
  padding: 110px 24px 120px;
  background: #e8ddd0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(176,125,58,0.4), transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(176,125,58,0.4), transparent);
  }
`

const MenuInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`

const MenuHeader = styled.div`
  text-align: center;
  margin-bottom: 64px;
`

const MenuEyebrow = styled.p`
  font-size: 1.3rem;
  letter-spacing: 0.45em;
  text-transform: uppercase;
  color: #2b1901;
  margin-bottom: 18px;
`

const MenuHeading = styled.h2`
  font-size: clamp(2.6rem, 5vw, 4.2rem);
  font-weight: 300;
  color: #2c1f14;
  margin: 0 auto 18px;
  line-height: 1.1;
  letter-spacing: -0.01em;
`

const MenuSubtitle = styled.p`
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  font-style: italic;
  color: #7a5c44;
  margin: 0 auto 32px;
  max-width: 560px;
  line-height: 1.8;
`

const MenuDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0 auto;
  max-width: 220px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b07d3a);
  }
  &::after {
    background: linear-gradient(90deg, #b07d3a, transparent);
  }
`

const GoldDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #b07d3a;
  flex-shrink: 0;
`

/* ─── Menu Image Frame ───────────────────────────────────────── */
const MenuImageFrame = styled.div`
  position: relative;
  max-width: 520px;
  margin: 56px auto 0;
`

const CornerTL = styled.div`
  position: absolute;
  top: -12px; left: -12px;
  width: 32px; height: 32px;
  border-top: 1px solid #b07d3a;
  border-left: 1px solid #b07d3a;
  z-index: 2;
  pointer-events: none;
`
const CornerTR = styled.div`
  position: absolute;
  top: -12px; right: -12px;
  width: 32px; height: 32px;
  border-top: 1px solid #b07d3a;
  border-right: 1px solid #b07d3a;
  z-index: 2;
  pointer-events: none;
`
const CornerBL = styled.div`
  position: absolute;
  bottom: -12px; left: -12px;
  width: 32px; height: 32px;
  border-bottom: 1px solid #b07d3a;
  border-left: 1px solid #b07d3a;
  z-index: 2;
  pointer-events: none;
`
const CornerBR = styled.div`
  position: absolute;
  bottom: -12px; right: -12px;
  width: 32px; height: 32px;
  border-bottom: 1px solid #b07d3a;
  border-right: 1px solid #b07d3a;
  z-index: 2;
  pointer-events: none;
`

const MenuImageCard = styled.div`
  border: 1px solid rgba(176, 125, 58, 0.25);
  border-radius: 3px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 24px 80px rgba(44, 31, 20, 0.15);
  cursor: zoom-in;
  position: relative;

  &:hover .zoom-hint {
    opacity: 1;
  }
`

const MenuImage = styled.img`
  width: 100%;
  display: block;
  object-fit: contain;
  transition: transform 0.4s ease;

  ${MenuImageCard}:hover & {
    transform: scale(1.02);
  }
`

const ZoomHint = styled.div`
  position: absolute;
  bottom: 14px;
  right: 14px;
  background: rgba(44, 31, 20, 0.72);
  color: #f5ede0;
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 7px 14px;
  border-radius: 2px;
  opacity: 0;
  transition: opacity 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
`

const MenuNote = styled.p`
  text-align: center;
  font-size: 0.88rem;
  color: #8a7060;
  font-style: italic;
  margin-top: 52px;
  letter-spacing: 0.02em;
  line-height: 1.7;
`

/* ─── Lightbox ───────────────────────────────────────────────── */
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 12, 6, 0.94);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.3s ease both;
  cursor: zoom-out;
`

const LightboxInner = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  animation: ${scaleIn} 0.35s ease both;
`

const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 88vh;
  object-fit: contain;
  display: block;
  border-radius: 2px;
  box-shadow: 0 40px 120px rgba(0,0,0,0.6);
`

const CloseButton = styled.button`
  position: absolute;
  top: -44px;
  right: 0;
  background: none;
  border: none;
  color: #f5ede0;
  font-size: 0.72rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover { opacity: 1; }
`

/* ─── Component ──────────────────────────────────────────────── */
const MENU_SRC = "https://i.postimg.cc/ZRNT4BLK/DINNER-MENU-of-LH-2.png"

const Diningscreen = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false)

  return (
    <PageWrapper>
      <Hero slides={SliderDataTwelve} />

      {/* Intro */}
      <IntroSection>
        <Eyebrow>Yadkin Valley, North Carolina</Eyebrow>
        <PageHeading>Yadkin Valley Dining</PageHeading>
        <OrnamentDivider><OrnamentDot /></OrnamentDivider>
        <IntroBody>
          The Yadkin Valley is North Carolina's first federally-recognized American
          Viticulture Area. Whether you want to eat small and local or experience
          fine dining, the Yadkin Valley will serve you exactly what you're looking for.
        </IntroBody>
      </IntroSection>

      {/* Explore Links */}
      <LinksSection>
        <LinksGrid>
          <LinkCard
            href="https://www.yadkinvalleync.com/attractions/?categories=&subcategories=fine-dining&town="
            target="_blank"
            rel="noopener noreferrer"
            delay="0s"
          >
            <div>
              <CardNumber>01 / 03</CardNumber>
              <CardTitle>Fine Dining</CardTitle>
              <CardDesc>
                Upscale restaurants and elevated cuisine spread across the Yadkin Valley wine country.
              </CardDesc>
            </div>
            <CardArrow className="card-arrow">Explore →</CardArrow>
          </LinkCard>

          <LinkCard
            href="https://www.tripadvisor.com/Restaurants-g49192-Hamptonville_North_Carolina.html"
            target="_blank"
            rel="noopener noreferrer"
            delay="0.1s"
          >
            <div>
              <CardNumber>02 / 03</CardNumber>
              <CardTitle>Local Dining</CardTitle>
              <CardDesc>
                Beloved local spots and hidden gems serving authentic Yadkin Valley flavors.
              </CardDesc>
            </div>
            <CardArrow className="card-arrow">Explore →</CardArrow>
          </LinkCard>

          <LinkCard
            href="https://www.visityadkin.com/dining"
            target="_blank"
            rel="noopener noreferrer"
            delay="0.2s"
          >
            <div>
              <CardNumber>03 / 03</CardNumber>
              <CardTitle>Yadkin County Dining</CardTitle>
              <CardDesc>
                A full guide to restaurants throughout Yadkin County — from casual bites to full evenings out.
              </CardDesc>
            </div>
            <CardArrow className="card-arrow">Explore →</CardArrow>
          </LinkCard>
        </LinksGrid>
      </LinksSection>

      {/* Dinner Menu */}
      <MenuSection>
        <MenuInner>
          <MenuHeader>
            <MenuEyebrow>Little Heaven Bed &amp; Breakfast</MenuEyebrow>
            <MenuHeading>Dinner Menu</MenuHeading>
            <MenuSubtitle>
              Crafted with love, rooted in tradition — Maria's kitchen brings
              the warmth of home to every plate.
            </MenuSubtitle>
            <MenuDivider><GoldDot /></MenuDivider>
          </MenuHeader>

          <MenuImageFrame>
            <CornerTL /><CornerTR /><CornerBL /><CornerBR />
            <MenuImageCard onClick={() => setLightboxOpen(true)}>
              <MenuImage
                src={MENU_SRC}
                alt="Little Heaven Bed & Breakfast Dinner Menu"
              />
              <ZoomHint className="zoom-hint">
                ⤢ View Full Screen
              </ZoomHint>
            </MenuImageCard>
          </MenuImageFrame>

          <MenuNote>
            Menu is subject to seasonal availability.<br />
            Contact us for current offerings and reservations.
          </MenuNote>
        </MenuInner>
      </MenuSection>

      {/* Lightbox */}
      {lightboxOpen && (
        <LightboxOverlay onClick={() => setLightboxOpen(false)}>
          <LightboxInner onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setLightboxOpen(false)}>
              ✕ Close
            </CloseButton>
            <LightboxImage
              src={MENU_SRC}
              alt="Little Heaven Bed & Breakfast Dinner Menu"
            />
          </LightboxInner>
        </LightboxOverlay>
      )}
    </PageWrapper>
  )
}

export default Diningscreen